import { useCallback, useSyncExternalStore, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Icon } from '@/lib/icons';
import { useAuth } from '@/lib/auth-context';
import { can } from '@brandschutz/shared';
import { PoweredByBIK } from '@/components/brand/PoweredByBIK';
import { Tooltip } from '@/components/ui/Tooltip';
import { useBranding } from '@/lib/branding';
import { cn } from '@/lib/cn';
import { FUSS_ITEMS, HAUPT_ITEMS, VERWALTUNG_ITEMS, type NavItem } from './nav-items';

const COLLAPSED_KEY = 'ignisai.sidebarCollapsed';
const VERWALTUNG_OPEN_KEY = 'ignisai.sidebarVerwaltungOpen';

/**
 * Etwas träger als der Tooltip-Standard (200 ms): beim Wandern über die
 * Menüliste sollen nicht acht Tooltips nacheinander aufblitzen.
 */
const TOOLTIP_DELAY = 320;

/**
 * Winziger Flag-Store für die gemerkten Sidebar-Zustände.
 *
 * Warum kein simples `useState` + `localStorage`: AppShell hält ZWEI
 * Sidebar-Instanzen gleichzeitig im Baum (feste Spalte + mobiler Drawer).
 * Mit lokalem State liefen deren Zustände auseinander, bis jemand neu lädt.
 * Der Cache ist die Wahrheit — schlägt localStorage fehl (Private Mode),
 * funktioniert die Bedienung weiter, sie überlebt nur den Reload nicht.
 */
const flagCache = new Map<string, boolean>();
const flagListeners = new Set<() => void>();

function getFlag(key: string, fallback: boolean): boolean {
  const cached = flagCache.get(key);
  if (cached !== undefined) return cached;
  let value = fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw !== null) value = raw === '1';
  } catch {
    /* Storage blockiert — Fallback bleibt stehen. */
  }
  flagCache.set(key, value);
  return value;
}

function setFlag(key: string, value: boolean): void {
  flagCache.set(key, value);
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* Storage blockiert — Zustand gilt dann nur für diese Session. */
  }
  for (const listener of flagListeners) listener();
}

function subscribeFlags(onChange: () => void): () => void {
  flagListeners.add(onChange);
  return () => {
    flagListeners.delete(onChange);
  };
}

function usePersistedFlag(key: string, fallback: boolean): [boolean, (next: boolean) => void] {
  const value = useSyncExternalStore(
    subscribeFlags,
    () => getFlag(key, fallback),
    () => fallback,
  );
  const set = useCallback((next: boolean) => setFlag(key, next), [key]);
  return [value, set];
}

interface SidebarProps {
  /**
   * Im mobilen Drawer wird nicht eingeklappt: dort ist die Leiste ohnehin
   * ein Overlay über die volle Höhe und der Hamburger in der TopBar blendet
   * sie komplett aus. Eine zusätzliche Icon-Leiste wäre dort nur verwirrend.
   */
  collapsible?: boolean;
}

export function Sidebar({ collapsible = true }: SidebarProps) {
  const { user } = useAuth();
  const branding = useBranding();
  const [collapsedPref, setCollapsed] = usePersistedFlag(COLLAPSED_KEY, false);
  const [verwaltungOpen, setVerwaltungOpen] = usePersistedFlag(VERWALTUNG_OPEN_KEY, true);

  const collapsed = collapsible && collapsedPref;

  const sichtbar = (items: NavItem[]): NavItem[] =>
    items.filter((item) => !item.permission || (user && can(user.role, item.permission)));

  const visibleItems = sichtbar(HAUPT_ITEMS);
  const verwaltungItems = sichtbar(VERWALTUNG_ITEMS);

  return (
    <aside
      className={cn(
        // Flächen-Tokens statt der festen navy-Skala: die ink-Rampe spiegelt
        // im Dunkelmodus, dadurch ist die Leiste im Hellmodus hell und im
        // Dunkelmodus dunkel — ohne eine einzige `dark:`-Variante.
        'flex h-screen shrink-0 flex-col border-r border-ink-200 bg-ink-100 text-ink-700',
        'transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Brand block — eingeklappt stapeln sich Logo und Umschalter, damit der
          Umschalter auch in der 64-px-Leiste erreichbar bleibt. */}
      <div
        className={cn(
          'flex border-b border-ink-200 py-5',
          collapsed ? 'flex-col items-center gap-3 px-2' : 'items-center gap-3 px-5',
        )}
      >
        {/* Ausgeklappt deutlich groesser (44 px) — das Zeichen war zu klein,
            um als Marke zu wirken. Eingeklappt bleibt es bei 36 px, weil die
            Leiste nur 64 px breit ist und daneben noch der Umschalter sitzt. */}
        <img
          src="/brand/ignisai-mark-256.png"
          alt="Ignis AI"
          width={collapsed ? 36 : 44}
          height={collapsed ? 36 : 44}
          className={cn('shrink-0 object-contain', collapsed ? 'h-9 w-9' : 'h-11 w-11')}
        />
        {collapsed ? null : (
          <div className="min-w-0 leading-none">
            <p className="text-base font-bold tracking-tight text-ink-900">
              Ignis<span className="text-accent-500"> AI</span>
            </p>
            <p className="mt-1 text-[9px] font-medium uppercase tracking-[0.18em] text-ink-500">
              Brandschutzmanagement
            </p>
          </div>
        )}
        {collapsible ? (
          <Tooltip
            placement="right"
            delay={TOOLTIP_DELAY}
            content={
              collapsed
                ? 'Klappt die Seitenleiste wieder auf die volle Breite mit Beschriftungen auf. Die Einstellung bleibt für den nächsten Besuch gespeichert.'
                : 'Klappt die Seitenleiste zu einer schmalen Icon-Leiste zusammen. Die Einstellung bleibt für den nächsten Besuch gespeichert.'
            }
          >
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? 'Menü ausklappen' : 'Menü einklappen'}
              aria-expanded={!collapsed}
              className={cn(
                'rounded p-1.5 text-ink-500 transition-colors hover:bg-ink-200 hover:text-ink-900',
                collapsed ? null : 'ml-auto',
              )}
            >
              <Icon
                name="ChevronRight"
                size={16}
                className={cn('transition-transform', collapsed ? null : 'rotate-180')}
              />
            </button>
          </Tooltip>
        ) : null}
      </div>

      {/* Nav */}
      <nav className={cn('flex-1 overflow-y-auto py-4', collapsed ? 'px-2' : 'px-3')}>
        <ul className="flex flex-col gap-0.5">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <SidebarLink {...item} collapsed={collapsed} />
            </li>
          ))}
          {verwaltungItems.length > 0 ? (
            <li className="mt-4 border-t border-ink-200 pt-3">
              <Tooltip
                placement="right"
                delay={TOOLTIP_DELAY}
                content={
                  <TooltipInhalt
                    titel="Verwaltung"
                    text="Stammdaten und Konfiguration: Vorlagen, Benutzerkonten, Änderungsprotokoll, Prüfregeln und das Erscheinungsbild der Anwendung. Der Block lässt sich zuklappen, wenn er nicht gebraucht wird."
                  />
                }
              >
                <button
                  type="button"
                  onClick={() => setVerwaltungOpen(!verwaltungOpen)}
                  aria-expanded={verwaltungOpen}
                  aria-label="Verwaltung"
                  className={cn(
                    'flex w-full items-center rounded text-[10px] font-medium uppercase tracking-widest text-ink-500 transition-colors hover:text-ink-900',
                    collapsed ? 'justify-center py-1' : 'gap-1 px-3 pb-1',
                  )}
                >
                  <Icon name={verwaltungOpen ? 'ChevronDown' : 'ChevronRight'} size={12} />
                  {collapsed ? null : <span>Verwaltung</span>}
                </button>
              </Tooltip>
              {verwaltungOpen ? (
                <div className="flex flex-col gap-0.5">
                  {verwaltungItems.map((item) => (
                    <SidebarLink key={item.to} {...item} collapsed={collapsed} />
                  ))}
                </div>
              ) : null}
            </li>
          ) : null}
        </ul>
      </nav>

      {/* Hilfe + Settings + Co-Branding-Footer */}
      <div
        className={cn(
          'flex flex-col gap-0.5 border-t border-ink-200 py-3',
          collapsed ? 'px-2' : 'px-3',
        )}
      >
        {FUSS_ITEMS.map((item) => (
          <SidebarLink key={item.to} {...item} collapsed={collapsed} />
        ))}
      </div>
      {/* Der Marken-Footer braucht Textbreite — in der Icon-Leiste würde er nur
          zu abgeschnittenen Wortfragmenten. Deshalb dort komplett weg. */}
      {collapsed ? null : (
        <div className="border-t border-ink-200 px-5 py-4">
          <p className="text-xs font-medium tracking-[0.2em] text-ink-700">{branding.officeName}</p>
          <p className="mt-0.5 text-[11px] text-ink-500">{branding.footerText}</p>
          <div className="mt-3 border-t border-ink-200 pt-2">
            <PoweredByBIK />
          </div>
          {user ? (
            <p className="mt-2 truncate text-[10px] text-ink-600" title={user.email}>
              {user.email}
            </p>
          ) : null}
        </div>
      )}
    </aside>
  );
}

/**
 * Tooltip-Inhalt mit fetter Kopfzeile — nötig, sobald das Label im UI fehlt
 * (eingeklappte Leiste) oder der Trigger selbst keinen Text trägt.
 */
function TooltipInhalt({ titel, text }: { titel: string; text: string }) {
  return (
    <>
      <span className="mb-0.5 block font-semibold">{titel}</span>
      <span className="block text-ink-300">{text}</span>
    </>
  );
}

function SidebarLink({
  to,
  label,
  icon,
  description,
  collapsed,
}: NavItem & { collapsed: boolean }) {
  // Ausgeklappt steht das Label daneben, der Tooltip ergänzt nur die
  // Erklärung. Eingeklappt fehlt es — dann muss er beides tragen.
  const tooltip: ReactNode = collapsed ? (
    <TooltipInhalt titel={label} text={description} />
  ) : (
    description
  );

  return (
    <Tooltip placement="right" delay={TOOLTIP_DELAY} content={tooltip}>
      <NavLink
        to={to}
        end={to === '/dashboard'}
        // Eingeklappt trägt das Icon allein keine Bedeutung — der Tooltip
        // erscheint zwar auch bei Tastaturfokus, der Name gehört aber
        // unabhängig davon an den Link selbst.
        aria-label={collapsed ? label : undefined}
        className={({ isActive }) =>
          cn(
            'group relative flex items-center rounded py-2 text-sm transition-colors',
            collapsed ? 'justify-center px-2' : 'gap-3 px-3',
            isActive
              ? 'bg-ink-200 text-ink-900'
              : 'text-ink-700 hover:bg-ink-200/70 hover:text-ink-900',
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive ? (
              <motion.span
                layoutId="sidebar-active-indicator"
                className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-accent-500"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            ) : null}
            <Icon
              name={icon}
              size={18}
              // accent-600 statt des festen Signal-Orange: 500 ist auf heller
              // Fläche zu blass. 600 spiegelt mit und liest sich in beiden Modi.
              className={cn(isActive ? 'text-accent-600' : 'text-ink-500 group-hover:text-ink-700')}
            />
            {collapsed ? null : <span className="truncate">{label}</span>}
          </>
        )}
      </NavLink>
    </Tooltip>
  );
}
